/**
 * Game State Manager — Single-device mode.
 * Manages the full game lifecycle locally in memory.
 * No network calls, no persistence beyond the session.
 */

import { Card, CardPoints, Deck, GameSettings, Player, PlayerOrder, RoundNumber, Team, TeamScores, VALIDATION } from '../shared/types';

// --- State Types ---

export type GamePhase =
  | 'lobby'
  | 'setup'
  | 'round-interstitial'
  | 'turn-waiting'
  | 'turn-active'
  | 'turn-end'
  | 'protest-resolution'
  | 'round-complete'
  | 'mid-round-transition'
  | 'game-end';

export interface TurnResult {
  cardTitle: string;
  cardPoints: CardPoints;
  action: 'correct' | 'skipped';
}

export interface ProtestItem {
  cardTitle: string;
  cardPoints: CardPoints;
  teamAVote: 'valid' | 'invalid' | null;
  teamBVote: 'valid' | 'invalid' | null;
  resolved: boolean;
  result: 'upheld' | 'dismissed' | null;
}

export interface TurnSummary {
  cardReaderName: string;
  team: Team;
  cardsCorrect: number;
  pointsEarned: number;
  cardsSkipped: number;
  pointsLost: number;
  results: TurnResult[];
  protests: ProtestItem[];
}

export interface GameState {
  phase: GamePhase;
  players: Player[];
  settings: GameSettings;
  deck: Card[];
  unplayedCards: Card[];
  currentRound: RoundNumber;
  currentTeam: Team;
  turnOrder: { A: string[]; B: string[] };
  turnIndex: { A: number; B: number };
  turnsTakenThisCycle: { A: number; B: number }; // how many turns taken in current cycle
  scores: TeamScores;
  roundScores: Record<number, TeamScores>;
  // Turn state
  currentCardIndex: number;
  previousCard: Card | null;
  previousAction: 'correct' | 'skipped' | null;
  turnResults: TurnResult[];
  timerRemaining: number;
  timerRunning: boolean;
  // Turn summary (between turns)
  lastTurnSummary: TurnSummary | null;
}

// --- Event listener system ---

type Listener = () => void;

const listeners: Set<Listener> = new Set();

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(): void {
  listeners.forEach((fn) => fn());
  // Auto-save to localStorage after every state change (during active gameplay)
  if (state.phase !== 'lobby' && state.phase !== 'setup') {
    saveToLocalStorage();
  }
}

// --- localStorage persistence ---

const SAVE_KEY = 'papelitos-single-game-save';

function saveToLocalStorage(): void {
  try {
    const serializable = {
      ...state,
      savedAt: Date.now(),
    };
    localStorage.setItem(SAVE_KEY, JSON.stringify(serializable));
  } catch { /* localStorage full or unavailable — ignore */ }
}

export function loadSavedGame(): (GameState & { savedAt: number }) | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Expire after 2 hours (game shouldn't last that long)
    if (Date.now() - parsed.savedAt > 2 * 60 * 60 * 1000) {
      clearSavedGame();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function hasSavedGame(): boolean {
  return loadSavedGame() !== null;
}

export function resumeSavedGame(): GamePhase | null {
  const saved = loadSavedGame();
  if (!saved) return null;
  // Restore state (strip the savedAt field)
  const { savedAt, ...gameState } = saved;
  state = gameState as GameState;
  // If resuming into turn-active, keep the timer running so it counts down
  // For other phases, timer should remain stopped
  if (state.phase !== 'turn-active') {
    state.timerRunning = false;
  } else {
    state.timerRunning = true;
  }
  notify();
  return state.phase;
}

export function clearSavedGame(): void {
  try { localStorage.removeItem(SAVE_KEY); } catch { /* ignore */ }
}

// --- State instance ---

let state: GameState = createInitialState();

export function getState(): Readonly<GameState> {
  return state;
}

function createInitialState(): GameState {
  return {
    phase: 'lobby',
    players: [],
    settings: {
      timerSeconds: 60,
      rounds: 3,
      playerOrder: 'shuffle',
      drawingRoundEnabled: false,
      deckId: '',
      cardsPerPlayer: 5,
    },
    deck: [],
    unplayedCards: [],
    currentRound: 1,
    currentTeam: 'A',
    turnOrder: { A: [], B: [] },
    turnIndex: { A: 0, B: 0 },
    turnsTakenThisCycle: { A: 0, B: 0 },
    scores: { A: 0, B: 0 },
    roundScores: { 1: { A: 0, B: 0 }, 2: { A: 0, B: 0 }, 3: { A: 0, B: 0 }, 4: { A: 0, B: 0 } },
    currentCardIndex: 0,
    previousCard: null,
    previousAction: null,
    turnResults: [],
    timerRemaining: 60,
    timerRunning: false,
    lastTurnSummary: null,
  };
}

export function resetGame(): void {
  state = createInitialState();
  clearSavedGame();
  notify();
}

// --- Lobby Actions ---

export function addPlayer(displayName: string, team: Team | null = null): string | null {
  if (state.players.length >= VALIDATION.MAX_PLAYERS) return 'Game is full (max 20 players)';
  if (displayName.length < VALIDATION.DISPLAY_NAME_MIN || displayName.length > VALIDATION.DISPLAY_NAME_MAX) {
    return 'Name must be 1-20 characters';
  }
  if (state.players.some((p) => p.displayName.toLowerCase() === displayName.toLowerCase())) {
    return 'Name already taken';
  }

  const id = crypto.randomUUID();
  state.players.push({
    id,
    displayName,
    team,
    isHost: state.players.length === 0,
  });
  notify();
  return null;
}

export function removePlayer(id: string): void {
  state.players = state.players.filter((p) => p.id !== id);
  notify();
}

export function assignTeam(playerId: string, team: Team): void {
  const player = state.players.find((p) => p.id === playerId);
  if (player) {
    player.team = team;
    notify();
  }
}

export function getTeamPlayers(team: Team): Player[] {
  return state.players.filter((p) => p.team === team);
}

export function validateLobby(): string | null {
  const teamA = getTeamPlayers('A');
  const teamB = getTeamPlayers('B');
  const unassigned = state.players.filter((p) => p.team === null);

  if (state.players.length < VALIDATION.MIN_PLAYERS) {
    return `Need at least ${VALIDATION.MIN_PLAYERS} players (have ${state.players.length})`;
  }
  if (unassigned.length > 0) {
    return `${unassigned.length} player(s) not assigned to a team`;
  }
  if (teamA.length < VALIDATION.MIN_PLAYERS_PER_TEAM) {
    return `Team A needs at least ${VALIDATION.MIN_PLAYERS_PER_TEAM} players`;
  }
  if (teamB.length < VALIDATION.MIN_PLAYERS_PER_TEAM) {
    return `Team B needs at least ${VALIDATION.MIN_PLAYERS_PER_TEAM} players`;
  }
  return null;
}

// --- Setup Actions ---

export function startSetup(): string | null {
  const error = validateLobby();
  if (error) return error;
  state.phase = 'setup';
  notify();
  return null;
}

export function updateSettings(partial: Partial<GameSettings>): void {
  state.settings = { ...state.settings, ...partial };
  notify();
}

export function selectDeck(deck: Deck): void {
  state.settings.deckId = deck.id;
  state.deck = [...deck.cards];
  notify();
}

// --- Game Start ---

export function startGame(): string | null {
  if (!state.settings.deckId || state.deck.length === 0) {
    return 'Please select a deck';
  }

  // Set rounds based on drawing round toggle
  state.settings.rounds = state.settings.drawingRoundEnabled ? 4 : 3;

  // Build turn order
  const teamA = getTeamPlayers('A').map((p) => p.id);
  const teamB = getTeamPlayers('B').map((p) => p.id);

  if (state.settings.playerOrder === 'shuffle') {
    shuffleArray(teamA);
    shuffleArray(teamB);
  }

  state.turnOrder = { A: teamA, B: teamB };
  state.turnIndex = { A: 0, B: 0 };
  state.turnsTakenThisCycle = { A: 0, B: 0 };

  // Trim deck based on cards-per-player
  const totalCards = state.players.length * state.settings.cardsPerPlayer;
  let gameDeck = [...state.deck];
  if (gameDeck.length > totalCards) {
    shuffleArray(gameDeck);
    gameDeck = gameDeck.slice(0, totalCards);
  }
  state.deck = gameDeck;

  // Initialize round
  state.currentRound = 1;
  state.currentTeam = 'A';
  state.unplayedCards = shuffleDeck([...state.deck]);
  state.scores = { A: 0, B: 0 };
  state.roundScores = { 1: { A: 0, B: 0 }, 2: { A: 0, B: 0 }, 3: { A: 0, B: 0 }, 4: { A: 0, B: 0 } };

  state.phase = 'round-interstitial';
  notify();
  return null;
}

// --- Round Transitions ---

export function startTurnWaiting(): void {
  state.phase = 'turn-waiting';
  notify();
}

export function startTurn(): void {
  state.phase = 'turn-active';
  state.timerRemaining = state.settings.timerSeconds;
  state.timerRunning = true;
  state.currentCardIndex = 0;
  state.previousCard = null;
  state.previousAction = null;
  state.turnResults = [];
  notify();
}

// --- Turn Actions ---

export function getCurrentCard(): Card | null {
  if (state.unplayedCards.length === 0) return null;
  return state.unplayedCards[0] || null;
}

export function getCurrentCardReader(): Player | null {
  const team = state.currentTeam;
  const playerId = state.turnOrder[team][state.turnIndex[team]];
  return state.players.find((p) => p.id === playerId) || null;
}

export function getNextCardReader(): Player | null {
  const nextTeam: Team = state.currentTeam === 'A' ? 'B' : 'A';
  const playerId = state.turnOrder[nextTeam][state.turnIndex[nextTeam]];
  return state.players.find((p) => p.id === playerId) || null;
}

/**
 * Returns the turn roster for a team showing who has gone and who's next.
 */
export interface TurnRosterEntry {
  player: Player;
  status: 'done' | 'current' | 'waiting';
}

export function getTurnRoster(team: Team): TurnRosterEntry[] {
  const order = state.turnOrder[team];
  const currentIdx = state.turnIndex[team];

  return order.map((playerId, i) => {
    const player = state.players.find((p) => p.id === playerId)!;
    let status: 'done' | 'current' | 'waiting';

    if (i < currentIdx) {
      status = 'done';
    } else if (i === currentIdx) {
      status = team === state.currentTeam ? 'current' : 'waiting';
    } else {
      status = 'waiting';
    }
    return { player, status };
  });
}

export function nextCard(): void {
  const card = getCurrentCard();
  if (!card) {
    endTurnDeckEmpty();
    return;
  }

  // Award points
  const points = card.points;
  state.scores[state.currentTeam] += points;
  state.roundScores[state.currentRound][state.currentTeam] += points;

  // Record result
  state.turnResults.push({ cardTitle: card.title, cardPoints: card.points, action: 'correct' });

  // Store for go-back
  state.previousCard = card;
  state.previousAction = 'correct';

  // Remove from unplayed
  state.unplayedCards.shift();

  // Check if deck is empty (round complete mid-turn)
  if (state.unplayedCards.length === 0) {
    advanceRoundMidTurn();
    return;
  }

  notify();
}

export function skipCard(): void {
  const card = getCurrentCard();
  if (!card) return;

  // Deduct points (min 0)
  const points = card.points;
  state.scores[state.currentTeam] = Math.max(0, state.scores[state.currentTeam] - points);
  state.roundScores[state.currentRound][state.currentTeam] = Math.max(
    0,
    state.roundScores[state.currentRound][state.currentTeam] - points
  );

  // Record result
  state.turnResults.push({ cardTitle: card.title, cardPoints: card.points, action: 'skipped' });

  // Store for go-back
  state.previousCard = card;
  state.previousAction = 'skipped';

  // Move to bottom of deck
  state.unplayedCards.shift();
  state.unplayedCards.push(card);

  notify();
}

export function goBack(): boolean {
  if (!state.previousCard || !state.previousAction) return false;

  const card = state.previousCard;

  // Reverse the score change
  if (state.previousAction === 'correct') {
    state.scores[state.currentTeam] = Math.max(0, state.scores[state.currentTeam] - card.points);
    state.roundScores[state.currentRound][state.currentTeam] = Math.max(
      0,
      state.roundScores[state.currentRound][state.currentTeam] - card.points
    );
    // Put card back at front of unplayed
    state.unplayedCards.unshift(card);
  } else if (state.previousAction === 'skipped') {
    state.scores[state.currentTeam] += card.points;
    state.roundScores[state.currentRound][state.currentTeam] += card.points;
    // Remove from bottom and put back at front
    state.unplayedCards.pop();
    state.unplayedCards.unshift(card);
  }

  // Remove last result
  state.turnResults.pop();

  // Clear go-back (can only go back once)
  state.previousCard = null;
  state.previousAction = null;

  notify();
  return true;
}

export function tickTimer(): void {
  if (!state.timerRunning) return;
  state.timerRemaining--;
  if (state.timerRemaining <= 0) {
    endTurnTimeUp();
  } else {
    notify();
  }
}

// --- Turn End ---

function endTurnTimeUp(): void {
  state.timerRunning = false;
  buildTurnSummary();
  state.phase = 'turn-end';
  notify();
}

function endTurnDeckEmpty(): void {
  state.timerRunning = false;
  buildTurnSummary();
  // If deck is empty, end round after turn summary
  state.phase = 'turn-end';
  notify();
}

function buildTurnSummary(): void {
  const cardReader = getCurrentCardReader();
  const correctResults = state.turnResults.filter((r) => r.action === 'correct');
  const skippedResults = state.turnResults.filter((r) => r.action === 'skipped');

  state.lastTurnSummary = {
    cardReaderName: cardReader?.displayName || 'Unknown',
    team: state.currentTeam,
    cardsCorrect: correctResults.length,
    pointsEarned: correctResults.reduce((sum, r) => sum + r.cardPoints, 0),
    cardsSkipped: skippedResults.length,
    pointsLost: skippedResults.reduce((sum, r) => sum + r.cardPoints, 0),
    results: [...state.turnResults],
    protests: correctResults.map((r) => ({
      cardTitle: r.cardTitle,
      cardPoints: r.cardPoints,
      teamAVote: null,
      teamBVote: null,
      resolved: false,
      result: null,
    })),
  };
}

// --- Protest Resolution ---

export function startProtestResolution(): void {
  state.phase = 'protest-resolution';
  notify();
}

export function voteProtest(protestIndex: number, team: Team, vote: 'valid' | 'invalid'): void {
  if (!state.lastTurnSummary) return;
  const protest = state.lastTurnSummary.protests[protestIndex];
  if (!protest || protest.resolved) return;

  if (team === 'A') protest.teamAVote = vote;
  else protest.teamBVote = vote;

  // Check if both teams have voted
  if (protest.teamAVote && protest.teamBVote) {
    if (protest.teamAVote === 'invalid' && protest.teamBVote === 'invalid') {
      // Both agree invalid — uphold protest
      protest.result = 'upheld';
      // Deduct points from the scoring team
      const scoringTeam = state.lastTurnSummary.team;
      state.scores[scoringTeam] = Math.max(0, state.scores[scoringTeam] - protest.cardPoints);
      state.roundScores[state.currentRound][scoringTeam] = Math.max(
        0,
        state.roundScores[state.currentRound][scoringTeam] - protest.cardPoints
      );
      // Return card to deck
      const card = state.deck.find((c) => c.title === protest.cardTitle);
      if (card) state.unplayedCards.push(card);
    } else if (protest.teamAVote === 'valid' && protest.teamBVote === 'valid') {
      // Both agree valid — dismiss
      protest.result = 'dismissed';
    } else {
      // Disagreement — mark as needing host tiebreaker
      // For now, leave unresolved until host decides
      return;
    }
    protest.resolved = true;
  }

  notify();
}

export function hostResolveProtest(protestIndex: number, decision: 'upheld' | 'dismissed'): void {
  if (!state.lastTurnSummary) return;
  const protest = state.lastTurnSummary.protests[protestIndex];
  if (!protest || protest.resolved) return;

  protest.result = decision;
  protest.resolved = true;

  if (decision === 'upheld') {
    const scoringTeam = state.lastTurnSummary.team;
    state.scores[scoringTeam] = Math.max(0, state.scores[scoringTeam] - protest.cardPoints);
    state.roundScores[state.currentRound][scoringTeam] = Math.max(
      0,
      state.roundScores[state.currentRound][scoringTeam] - protest.cardPoints
    );
    const card = state.deck.find((c) => c.title === protest.cardTitle);
    if (card) state.unplayedCards.push(card);
  }

  notify();
}

export function skipProtests(): void {
  // Dismiss all unresolved protests
  if (state.lastTurnSummary) {
    state.lastTurnSummary.protests.forEach((p) => {
      if (!p.resolved) {
        p.resolved = true;
        p.result = 'dismissed';
      }
    });
  }
  advanceTurn();
}

// --- Advance Turn ---

export function advanceTurn(): void {
  // Advance the CURRENT team's index (they just finished their turn)
  const currentTeamOrder = state.turnOrder[state.currentTeam];
  state.turnIndex[state.currentTeam] = (state.turnIndex[state.currentTeam] + 1) % currentTeamOrder.length;

  // Track that current team took a turn
  state.turnsTakenThisCycle[state.currentTeam]++;

  // Reset cycle counter if all players in both teams have gone
  const teamASize = state.turnOrder.A.length;
  const teamBSize = state.turnOrder.B.length;
  if (state.turnsTakenThisCycle.A >= teamASize && state.turnsTakenThisCycle.B >= teamBSize) {
    state.turnsTakenThisCycle = { A: 0, B: 0 };
  }

  // Check if deck is empty → round complete
  if (state.unplayedCards.length === 0) {
    endRound();
    return;
  }

  // Switch to the other team (their index is already pointing to the next player)
  state.currentTeam = state.currentTeam === 'A' ? 'B' : 'A';

  state.phase = 'turn-waiting';
  notify();
}

// --- Round End ---

function endRound(): void {
  state.timerRunning = false;
  state.phase = 'round-complete';
  notify();
}

/**
 * Called when all cards are guessed mid-turn.
 * Saves remaining time, advances round, reshuffles deck,
 * pauses in 'mid-round-transition' phase so the player can see new rules,
 * then resumes with saved time when they press START.
 * If it's the last round, ends the game instead.
 */
function advanceRoundMidTurn(): void {
  const maxRounds = state.settings.rounds;
  if (state.currentRound >= maxRounds) {
    // Last round complete — show round-complete then game-end
    state.timerRunning = false;
    buildTurnSummary();
    state.phase = 'round-complete';
    notify();
    return;
  }

  // 1. Save remaining time
  // (timerRemaining already holds the current value)

  // 2. Stop the timer
  state.timerRunning = false;

  // 3. Advance to next round
  state.currentRound = (state.currentRound + 1) as RoundNumber;

  // 4. Reshuffle the full deck for the new round
  state.unplayedCards = shuffleDeck([...state.deck]);

  // 5. Keep same player and team (don't change currentTeam or turnIndex)

  // Clear previous card (new round, fresh start on go-back)
  state.previousCard = null;
  state.previousAction = null;
  state.turnResults = [];

  // 6. Show mid-round transition screen (player presses START to resume)
  state.phase = 'mid-round-transition';
  notify();
}

/**
 * Called when the player presses START after mid-round transition.
 * Resumes their turn with the saved remaining time.
 */
export function resumeTurnAfterMidRound(): void {
  state.phase = 'turn-active';
  state.timerRunning = true;
  // timerRemaining already has the saved value
  notify();
}

export function advanceRound(): void {
  const maxRounds = state.settings.rounds;
  if (state.currentRound >= maxRounds) {
    // Game over
    state.phase = 'game-end';
    notify();
    return;
  }

  // Advance to next round
  state.currentRound = (state.currentRound + 1) as RoundNumber;

  // Reshuffle the full deck for the new round
  state.unplayedCards = shuffleDeck([...state.deck]);

  // Turn order stays the same across rounds (fixed at game start)
  // Just reset the index to start from the beginning
  state.turnIndex = { A: 0, B: 0 };
  state.turnsTakenThisCycle = { A: 0, B: 0 };

  // Alternate which team starts (opposite of who started last round)
  state.currentTeam = state.currentTeam === 'A' ? 'B' : 'A';

  state.phase = 'round-interstitial';
  notify();
}

// --- Utilities ---

function shuffleArray<T>(arr: T[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

function shuffleDeck(cards: Card[]): Card[] {
  const shuffled = [...cards];
  shuffleArray(shuffled);
  return shuffled;
}
